import { type IdentityProvider } from './types';
import { baseUrl } from './utils';

/** Submits a TOTP code for validation */
export async function validateTotpCodeAtLogin(code: string): Promise<boolean> {
  const response = await fetch(`${baseUrl.authms}/totp/validate?login=true`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });

  if (!response.ok) {
    throw new Error();
  }

  const data = (await response.json()) as { status: 'VALID' | 'INVALID ' };

  return data.status === 'VALID';
}

interface IDPResponse {
  identityProviders: {
    displayName?: string;
    name?: string;
    protocol?: 'saml' | 'unknown';
    enabled?: boolean;
    testMode?: boolean;
  }[];
}

/**
 * Fetches a list of provides
 */
export async function getIdentityProviders(): Promise<IdentityProvider[]> {
  const response = await fetch(`${baseUrl.authms}/v1/sso/identity-providers`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (response.status !== 200) {
    throw new Error();
  }

  const { identityProviders } = (await response.json()) as IDPResponse;

  return identityProviders.map((provider) => ({
    // Fallback in case displayName is not configured
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    name: (provider.displayName || provider.name) ?? '',
    alias: provider.name ?? '',
    protocol: provider.protocol ?? 'unknown',
    isEnabled: provider.enabled ?? false,
    isTestMode: provider.testMode ?? false,
  }));
}

/** Check user authentication status */
export async function getAuthenticationStatus(): Promise<{
  isAuthenticated: boolean;
}> {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_AUTH_API ?? ''}/ECM/api/v5/getEcmVersion`
    );

    if (response.status === 401) {
      return { isAuthenticated: false };
    }

    if (!response.ok) {
      throw new Error(`Request was unsuccessful: ${response.statusText}`);
    }

    return { isAuthenticated: true };
  } catch (e) {
    return { isAuthenticated: false };
  }
}
